import { Box, Drawer, Group, Text, UnstyledButton } from '@mantine/core';
import { X } from 'lucide-react';
import { type ReactElement, type ReactNode } from 'react';
import InitialAvatar from './InitialAvatar';

interface DetailDrawerProps {
    opened: boolean;
    onClose: () => void;
    /** Text the header avatar's initial is derived from. */
    avatarSource: string;
    title: ReactNode;
    subtitle?: ReactNode;
    /** Row of summary tiles under the header (e.g. `StatTile`s). */
    stats?: ReactNode;
    size?: number | string;
    /** When false, the body neither pads nor scrolls — children own layout and scrolling (e.g. a fixed tab strip over scrolling panels). */
    scrollBody?: boolean;
    children: ReactNode;
}

/**
 * Right-side entity detail drawer: avatar + title/subtitle header with a close
 * button, an optional stat-tile row, and a scrollable body. Shared by the user
 * and directory drawers.
 */
export default function DetailDrawer({
    opened,
    onClose,
    avatarSource,
    title,
    subtitle,
    stats,
    size = 560,
    scrollBody = true,
    children,
}: DetailDrawerProps): ReactElement {
    return (
        <Drawer
            opened={opened}
            onClose={onClose}
            position="right"
            size={size}
            title={null}
            withCloseButton={false}
            overlayProps={{ backgroundOpacity: 0.3, blur: 2 }}
            transitionProps={{ transition: 'slide-left', duration: 250 }}
            styles={{
                body: { padding: 0, display: 'flex', flexDirection: 'column', height: '100%' },
                content: { display: 'flex', flexDirection: 'column' },
            }}
        >
            <Box p="lg" style={{ borderBottom: '1px solid var(--bm-color-border)' }}>
                <Group justify="space-between" align="flex-start" mb={stats ? 'lg' : 0}>
                    <Group gap="md">
                        <InitialAvatar source={avatarSource} size={48} variant="filled" />
                        <Box>
                            <Text size="lg" fw={700}>
                                {title}
                            </Text>
                            {subtitle && (
                                <Text size="xs" c="dimmed">
                                    {subtitle}
                                </Text>
                            )}
                        </Box>
                    </Group>
                    <UnstyledButton onClick={onClose} aria-label="Close" style={{ color: 'var(--bm-color-text-dimmed)', padding: 4 }}>
                        <X size={20} />
                    </UnstyledButton>
                </Group>
                {stats && (
                    <Group grow gap="sm">
                        {stats}
                    </Group>
                )}
            </Box>
            {scrollBody ?
                <Box p="lg" style={{ flex: 1, overflowY: 'auto' }}>
                    {children}
                </Box>
            :   <Box style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>{children}</Box>}
        </Drawer>
    );
}
